import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  adminApproveInviteRequest,
  adminInviteUser,
  adminLinkStudentParent,
  adminLinkStudentTutor,
  adminListInviteRequests,
  adminListUsers,
  adminRejectInviteRequest,
  adminUnlinkStudentParent,
  adminUnlinkStudentTutor,
  adminUpdateUser,
} from '../api/admin';
import { getStoredUser, parseJsonSafe } from '../api/client';
import UserSearchSelect from '../components/UserSearchSelect';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return String(value);
  }
  return dt.toLocaleString();
}

function AdminUsersPage() {
  const currentUser = getStoredUser();
  const [users, setUsers] = useState([]);
  const [inviteRequests, setInviteRequests] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingInviteRequests, setIsLoadingInviteRequests] = useState(true);
  const [usersErrorMessage, setUsersErrorMessage] = useState('');
  const [inviteRequestsErrorMessage, setInviteRequestsErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [invitingUserId, setInvitingUserId] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [studentIdDrafts, setStudentIdDrafts] = useState({});
  const [actingInviteRequestId, setActingInviteRequestId] = useState(null);
  const [inviteDisplayNames, setInviteDisplayNames] = useState({});
  const [inviteDateOfBirths, setInviteDateOfBirths] = useState({});
  const [studentIdToManage, setStudentIdToManage] = useState('');
  const [selectedManageTutorUser, setSelectedManageTutorUser] = useState(null);
  const [selectedManageParents, setSelectedManageParents] = useState([]);
  const [isManagingLinks, setIsManagingLinks] = useState(false);

  const loadUsers = useCallback(async () => {
    setUsersErrorMessage('');
    setIsLoadingUsers(true);
    try {
      const response = await adminListUsers();
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to load users.'));
      }
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to load users.');
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const loadInviteRequests = useCallback(async () => {
    setInviteRequestsErrorMessage('');
    setIsLoadingInviteRequests(true);
    try {
      const response = await adminListInviteRequests({ status: 'PENDING', limit: 200 });
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to load invite requests.'));
      }
      setInviteRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      setInviteRequestsErrorMessage(error.message || 'Failed to load invite requests.');
    } finally {
      setIsLoadingInviteRequests(false);
    }
  }, []);

  const loadAdminData = useCallback(async () => {
    await Promise.all([loadInviteRequests(), loadUsers()]);
  }, [loadInviteRequests, loadUsers]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleInviteUser = async (userId) => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    setInvitingUserId(userId);
    try {
      const response = await adminInviteUser(userId);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to send invite.'));
      }
      setNoticeMessage(
        `Invite sent for user ${userId}. This can recover expired or incomplete invites.`,
      );
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to send invite.');
    } finally {
      setInvitingUserId(null);
    }
  };

  const applyUserUpdate = async (user, payload, successMessage) => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    setUpdatingUserId(user.id);
    try {
      const response = await adminUpdateUser(user.id, payload);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to update user.'));
      }
      setNoticeMessage(successMessage);
      await loadUsers();
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to update user.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleActiveChange = async (user) => {
    const nextIsActive = !user.is_active;
    const verb = nextIsActive ? 'reactivate' : 'deactivate';
    const warning = nextIsActive
      ? `Reactivate ${user.email || `user ${user.id}`}? They will regain account access.`
      : `Deactivate ${user.email || `user ${user.id}`}? This does not delete their data, and their email address remains taken.`;
    if (!window.confirm(warning)) {
      return;
    }
    await applyUserUpdate(
      user,
      { is_active: nextIsActive },
      `${user.email || `User ${user.id}`} ${verb}d. The change is recorded in the audit log.`,
    );
  };

  const handleRoleChange = async (user) => {
    const nextRole = roleDrafts[user.id] || user.role;
    if (nextRole === user.role) {
      return;
    }

    const payload = { role: nextRole };
    if (nextRole === 'STUDENT') {
      const rawStudentId = String(studentIdDrafts[user.id] ?? user.student_id ?? '').trim();
      if (!/^\d+$/.test(rawStudentId)) {
        setUsersErrorMessage('A numeric student id is required when changing a user to STUDENT.');
        return;
      }
      payload.student_id = Number(rawStudentId);
    } else {
      payload.student_id = null;
    }

    const identity = user.email || `user ${user.id}`;
    if (!window.confirm(`Change ${identity} from ${user.role} to ${nextRole}?`)) {
      return;
    }
    await applyUserUpdate(
      user,
      payload,
      `${identity} changed from ${user.role} to ${nextRole}. The change is recorded in the audit log.`,
    );
  };

  const handleApproveInviteRequest = async (requestItem) => {
    setNoticeMessage('');
    setInviteRequestsErrorMessage('');
    setActingInviteRequestId(requestItem.id);

    const payload = {};
    const requiresDisplayName =
      requestItem.role === 'STUDENT' && !String(requestItem.student_display_name || '').trim();
    const enteredDisplayName = String(inviteDisplayNames[requestItem.id] || '').trim();
    if (requiresDisplayName && !enteredDisplayName) {
      setInviteRequestsErrorMessage('Student display name is required to approve this request.');
      setActingInviteRequestId(null);
      return;
    }
    if (enteredDisplayName) {
      payload.student_display_name = enteredDisplayName;
    }
    if (requestItem.role === 'STUDENT') {
      const enteredDateOfBirth = String(inviteDateOfBirths[requestItem.id] || '').trim();
      if (!enteredDateOfBirth) {
        setInviteRequestsErrorMessage('Student date of birth is required to approve this request.');
        setActingInviteRequestId(null);
        return;
      }
      payload.date_of_birth = enteredDateOfBirth;
    }

    try {
      const response = await adminApproveInviteRequest(requestItem.id, payload);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to approve invite request.'));
      }
      setNoticeMessage(`Invite request approved for ${requestItem.email}.`);
      setInviteDateOfBirths((prev) => {
        const next = { ...prev };
        delete next[requestItem.id];
        return next;
      });
      await loadAdminData();
    } catch (error) {
      setInviteRequestsErrorMessage(error.message || 'Failed to approve invite request.');
    } finally {
      setActingInviteRequestId(null);
    }
  };

  const parseManagedStudentId = () => {
    const trimmed = studentIdToManage.trim();
    if (!/^\d+$/.test(trimmed)) {
      setUsersErrorMessage('Student id is required and must be numeric.');
      return null;
    }
    return Number(trimmed);
  };

  const handleLinkTutor = async () => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    const studentId = parseManagedStudentId();
    if (studentId === null) {
      return;
    }
    if (!selectedManageTutorUser?.id) {
      setUsersErrorMessage('Select a tutor to link.');
      return;
    }
    setIsManagingLinks(true);
    try {
      const response = await adminLinkStudentTutor(studentId, Number(selectedManageTutorUser.id));
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to link tutor.'));
      }
      setNoticeMessage(`Tutor ${selectedManageTutorUser.id} linked to student ${studentId}.`);
      await loadAdminData();
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to link tutor.');
    } finally {
      setIsManagingLinks(false);
    }
  };

  const handleUnlinkTutor = async () => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    const studentId = parseManagedStudentId();
    if (studentId === null) {
      return;
    }
    setIsManagingLinks(true);
    try {
      const response = await adminUnlinkStudentTutor(studentId);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to unlink tutor.'));
      }
      setNoticeMessage(`Tutor unlinked from student ${studentId}.`);
      setSelectedManageTutorUser(null);
      await loadAdminData();
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to unlink tutor.');
    } finally {
      setIsManagingLinks(false);
    }
  };

  const handleLinkSelectedParents = async () => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    const studentId = parseManagedStudentId();
    if (studentId === null) {
      return;
    }
    if (!selectedManageParents.length) {
      setUsersErrorMessage('Select at least one parent to link.');
      return;
    }

    setIsManagingLinks(true);
    try {
      for (let index = 0; index < selectedManageParents.length; index += 1) {
        const parent = selectedManageParents[index];
        setNoticeMessage(
          `Linking parent ${index + 1} of ${selectedManageParents.length} (id: ${parent.id})...`,
        );
        const response = await adminLinkStudentParent(studentId, Number(parent.id));
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, `Failed to link parent ${parent.id}.`));
        }
      }
      setNoticeMessage(
        `Linked ${selectedManageParents.length} parent(s) to student ${studentId}.`,
      );
      setSelectedManageParents([]);
      await loadAdminData();
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to link selected parents.');
    } finally {
      setIsManagingLinks(false);
    }
  };

  const handleUnlinkSelectedParents = async () => {
    setNoticeMessage('');
    setUsersErrorMessage('');
    const studentId = parseManagedStudentId();
    if (studentId === null) {
      return;
    }
    if (!selectedManageParents.length) {
      setUsersErrorMessage('Select at least one parent to unlink.');
      return;
    }

    setIsManagingLinks(true);
    try {
      for (let index = 0; index < selectedManageParents.length; index += 1) {
        const parent = selectedManageParents[index];
        setNoticeMessage(
          `Unlinking parent ${index + 1} of ${selectedManageParents.length} (id: ${parent.id})...`,
        );
        const response = await adminUnlinkStudentParent(studentId, Number(parent.id));
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, `Failed to unlink parent ${parent.id}.`));
        }
      }
      setNoticeMessage(
        `Unlinked ${selectedManageParents.length} parent(s) from student ${studentId}.`,
      );
      setSelectedManageParents([]);
      await loadAdminData();
    } catch (error) {
      setUsersErrorMessage(error.message || 'Failed to unlink selected parents.');
    } finally {
      setIsManagingLinks(false);
    }
  };

  const handleRejectInviteRequest = async (requestId) => {
    setNoticeMessage('');
    setInviteRequestsErrorMessage('');
    setActingInviteRequestId(requestId);
    try {
      const response = await adminRejectInviteRequest(requestId);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to reject invite request.'));
      }
      setNoticeMessage('Invite request rejected.');
      await loadAdminData();
    } catch (error) {
      setInviteRequestsErrorMessage(error.message || 'Failed to reject invite request.');
    } finally {
      setActingInviteRequestId(null);
    }
  };

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Admin workspace</p>
          <h1>Admin: Users</h1>
          {currentUser?.email ? <p className="muted-text">Signed in as {currentUser.email}</p> : null}
          <p>Manage onboarding, linking, invite approvals, and resend flows from the supported admin surface.</p>
        </div>
        <div className="page-header-actions">
          <Link to="/admin/users/create" className="btn btn-primary">Open user creation wizard</Link>
        </div>
      </section>
      {noticeMessage ? <p role="status" className="notice notice-info">{noticeMessage}</p> : null}
      <section className="panel-grid panel-grid-two-up">
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Onboarding</p>
              <h2>User creation</h2>
            </div>
          </div>
          <div className="stack-md">
            <p className="muted-text">
              Create tutor, student, and parent accounts through the documented user creation wizard.
              Admin linking and resend flows remain on this page.
            </p>
            <p>
              <Link to="/admin/users/create" className="btn btn-primary">Open user creation wizard</Link>
            </p>
          </div>
        </section>
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Relationships</p>
              <h2>Manage student links</h2>
            </div>
          </div>
        <div className="stack-md">
          <div>
            <label htmlFor="manage-student-id">Student id</label>
            <input
              id="manage-student-id"
              type="number"
              value={studentIdToManage}
              onChange={(event) => setStudentIdToManage(event.target.value)}
              required
            />
          </div>
          <div>
            <UserSearchSelect
              label="Search tutor to link (email)"
              placeholder="Type tutor email"
              role="TUTOR"
              multiple={false}
              selected={selectedManageTutorUser}
              onChange={setSelectedManageTutorUser}
              helpText="Select one tutor to link."
            />
          </div>
          <div className="stack-sm">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleLinkTutor}
              disabled={isManagingLinks}
            >
              {isManagingLinks ? 'Working...' : 'Link tutor'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleUnlinkTutor}
              disabled={isManagingLinks}
            >
              {isManagingLinks ? 'Working...' : 'Unlink tutor'}
            </button>
          </div>
          <div>
            <UserSearchSelect
              label="Search parents to link/unlink (email)"
              placeholder="Type parent email"
              role="PARENT"
              multiple
              selected={selectedManageParents}
              onChange={setSelectedManageParents}
              helpText="Select one or more parents to link or unlink."
            />
          </div>
          <div className="stack-sm">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleLinkSelectedParents}
              disabled={isManagingLinks}
            >
              {isManagingLinks ? 'Working...' : 'Link selected parents'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleUnlinkSelectedParents}
              disabled={isManagingLinks}
            >
              {isManagingLinks ? 'Working...' : 'Unlink selected parents'}
            </button>
          </div>
        </div>
        </section>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Approvals</p>
            <h2>Invite requests</h2>
          </div>
        </div>
        {isLoadingInviteRequests ? <p>Loading invite requests...</p> : null}
        {!isLoadingInviteRequests ? (
          <ul className="data-list">
            {inviteRequests.map((requestItem) => {
              const needsDisplayName =
                requestItem.role === 'STUDENT' && !String(requestItem.student_display_name || '').trim();
              const isActing = actingInviteRequestId === requestItem.id;
              return (
                <li key={`invite-request-${requestItem.id}`} className="data-list-item">
                  <p>
                    email: {requestItem.email} | role: {requestItem.role} | created: {formatDateTime(requestItem.created_at)}
                  </p>
                  {needsDisplayName ? (
                    <div>
                      <label htmlFor={`invite-request-display-name-${requestItem.id}`}>Student display name</label>
                      <input
                        id={`invite-request-display-name-${requestItem.id}`}
                        type="text"
                        value={inviteDisplayNames[requestItem.id] || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setInviteDisplayNames((prev) => ({ ...prev, [requestItem.id]: value }));
                        }}
                        placeholder="Required for student"
                      />
                    </div>
                  ) : null}
                  {requestItem.role === 'STUDENT' ? (
                    <div>
                      <label htmlFor={`invite-request-date-of-birth-${requestItem.id}`}>Student date of birth</label>
                      <input
                        id={`invite-request-date-of-birth-${requestItem.id}`}
                        type="date"
                        value={inviteDateOfBirths[requestItem.id] || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setInviteDateOfBirths((prev) => ({ ...prev, [requestItem.id]: value }));
                        }}
                        required
                      />
                    </div>
                  ) : null}
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isActing}
                      onClick={() => handleApproveInviteRequest(requestItem)}
                    >
                      {isActing ? 'Processing...' : 'Approve'}
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isActing}
                      onClick={() => handleRejectInviteRequest(requestItem.id)}
                    >
                      {isActing ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {!isLoadingInviteRequests && inviteRequests.length === 0 ? (
          <p className="muted-text">No pending invite requests.</p>
        ) : null}
        {inviteRequestsErrorMessage ? (
          <p role="alert" className="notice notice-error">{inviteRequestsErrorMessage}</p>
        ) : null}
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Directory</p>
            <h2>Users</h2>
          </div>
        </div>
        <p className="muted-text">
          Deactivation is reversible and does not delete user data. A deactivated account's email address
          remains taken and cannot be reused for another account. Account and role changes are recorded in
          the <Link to="/admin/audit">audit log</Link>.
        </p>
        {isLoadingUsers ? <p>Loading users...</p> : null}
        {!isLoadingUsers ? (
          <ul className="data-list">
            {users.map((user) => {
              const selectedRole = roleDrafts[user.id] || user.role;
              const isUpdating = updatingUserId === user.id;
              return (
              <li key={user.id} className="data-list-item">
                <p>
                  id: {user.id} | email: {user.email || '-'} | current role: <strong>{user.role || '-'}</strong> | status:{' '}
                  <strong>{user.is_active ? 'ACTIVE' : 'DEACTIVATED'}</strong> | student_id: {user.student_id ?? '-'}
                </p>
                {user.guardian_pending ? (
                  <p role="status" className="notice notice-warning">
                    Guardian pending — student access is locked until a parent accepts their invite.
                  </p>
                ) : null}
                <div className="stack-sm">
                  <div>
                    <label htmlFor={`user-role-${user.id}`}>Change role</label>
                    <select
                      id={`user-role-${user.id}`}
                      value={selectedRole}
                      disabled={isUpdating}
                      onChange={(event) => setRoleDrafts((previous) => ({
                        ...previous,
                        [user.id]: event.target.value,
                      }))}
                    >
                      {['ADMIN', 'TUTOR', 'STUDENT', 'PARENT'].map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  {selectedRole === 'STUDENT' ? (
                    <div>
                      <label htmlFor={`user-student-id-${user.id}`}>Student id</label>
                      <input
                        id={`user-student-id-${user.id}`}
                        type="number"
                        min="1"
                        value={studentIdDrafts[user.id] ?? user.student_id ?? ''}
                        disabled={isUpdating}
                        onChange={(event) => setStudentIdDrafts((previous) => ({
                          ...previous,
                          [user.id]: event.target.value,
                        }))}
                      />
                    </div>
                  ) : null}
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isUpdating || selectedRole === user.role}
                      onClick={() => handleRoleChange(user)}
                    >
                      {isUpdating ? 'Updating...' : 'Apply role change'}
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isUpdating}
                      onClick={() => handleActiveChange(user)}
                    >
                      {isUpdating ? 'Updating...' : user.is_active ? 'Deactivate account' : 'Reactivate account'}
                    </button>
                  </div>
                </div>
                {user.id === currentUser?.id ? <p className="muted-text">(current account)</p> : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={invitingUserId === user.id}
                    onClick={() => handleInviteUser(user.id)}
                  >
                    {invitingUserId === user.id ? 'Sending invite...' : 'Send/Resend invite link'}
                  </button>
                )}
              </li>
              );
            })}
          </ul>
        ) : null}
        {!isLoadingUsers && users.length === 0 ? <p className="muted-text">No users found.</p> : null}
        {usersErrorMessage ? <p role="alert" className="notice notice-error">{usersErrorMessage}</p> : null}
      </section>
    </div>
  );
}

export default AdminUsersPage;
